import React, {useEffect, useState} from "react";

import { observer, inject } from "mobx-react";
import API from "../utils/api";
import {api2ActionLogs} from "../utils/operations";
import {toast} from "react-toastify";
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  TablePagination
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import {dateTimeFormatter} from "../utils/misc";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Loader from "../components/Loader";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";

const StyledTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(142, 195, 242, 0.4)',
    },
    '&:hover': {
      backgroundColor: '#f5f5f5',
    }
  },
}))(TableRow);

const ATTENDANCE_COLORS = {
  present:  '#2e7d32',
  sick:     '#c62828',
  vacation: '#1565c0',
  absentee: '#e65100',
};

const ChangesCell = ({ data }) => {
  if (!data || !data.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {data.map((item, i) => {
        // structured field change: { field, from, to }
        if (item && typeof item === 'object' && 'from' in item && 'to' in item) {
          return (
            <div key={i} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              {item.field && (
                <span style={{ color: '#555', fontWeight: 600, marginRight: 2 }}>{item.field}:</span>
              )}
              {item.date && (
                <span style={{ color: '#888', marginRight: 4 }}>{item.date}</span>
              )}
              <span style={{
                color: ATTENDANCE_COLORS[item.from] || '#c62828',
                background: 'rgba(198,40,40,0.08)',
                borderRadius: 3,
                padding: '1px 5px',
                textDecoration: 'line-through',
              }}>
                {item.from || '(empty)'}
              </span>
              <span style={{ color: '#999' }}>→</span>
              <span style={{
                color: ATTENDANCE_COLORS[item.to] || '#2e7d32',
                background: 'rgba(46,125,50,0.08)',
                borderRadius: 3,
                padding: '1px 5px',
              }}>
                {item.to || '(empty)'}
              </span>
            </div>
          );
        }

        // program toggle: { action, plan }
        if (item && typeof item === 'object' && 'action' in item) {
          const assigned = item.action === 'assigned';
          return (
            <div key={i} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{
                color: assigned ? '#2e7d32' : '#c62828',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1,
              }}>
                {assigned ? '+' : '−'}
              </span>
              <span style={{ color: assigned ? '#2e7d32' : '#c62828' }}>{item.plan}</span>
            </div>
          );
        }

        // simple field+value: { field, value }
        if (item && typeof item === 'object' && 'field' in item) {
          return (
            <div key={i} style={{ fontSize: 13 }}>
              <span style={{ color: '#555', fontWeight: 600 }}>{item.field}: </span>
              <span>{item.value}</span>
            </div>
          );
        }

        // fallback: legacy plain string
        return <div key={i} style={{ fontSize: 13 }}>{String(item)}</div>;
      })}
    </div>
  );
};

const ACTION_TYPE_LABELS = {
  child_creation: 'Add Child',
  child_update: 'Edit Child',
  child_deletion: 'Delete Child',
  child_attendance: 'Attendance Update',
  child_program: 'Program Toggle',
};

const defaultFilter = {
  date_from: null,
  date_to: null,
  child_name: '',
  type: '',
};

const ActionLogs = ({ Auth, history }) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState(defaultFilter);
  const [appliedFilter, setAppliedFilter] = useState(defaultFilter);

  useEffect(() => {
    if (!Auth.can('logs')) { history.push("/children"); }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    setPage(0);
    setAppliedFilter(filter);
  };

  const handleClearFilters = () => {
    setFilter(defaultFilter);
    setPage(0);
    setAppliedFilter(defaultFilter);
  };

  const loadData = async (currentFilter, currentPage) => {
    setLoading(true);
    try {
      const params = { page: currentPage + 1 };
      if (currentFilter.date_from) params.date_from = currentFilter.date_from.toISOString().slice(0, 10);
      if (currentFilter.date_to) params.date_to = currentFilter.date_to.toISOString().slice(0, 10);
      if (currentFilter.child_name) params.child_name = currentFilter.child_name;
      if (currentFilter.type) params.type = currentFilter.type;

      const response = await API.get(api2ActionLogs, { params });
      setLogs(response.data.data);
      setTotal(response.data.total || 0);
    } catch (e) {
      toast.error('Error loading logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(appliedFilter, page);
  }, [appliedFilter, page]);

  return (
    <div className="actionLogs">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <form onSubmit={handleFormSubmit}>
          <div className="filters" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
            <DatePicker
              slotProps={{ textField: { size: 'small', autoComplete: 'off' } }}
              label="Date From"
              value={filter.date_from}
              onChange={(val) => setFilter(prev => ({ ...prev, date_from: val }))}
            />
            <DatePicker
              slotProps={{ textField: { size: 'small', autoComplete: 'off' } }}
              label="Date To"
              value={filter.date_to}
              onChange={(val) => setFilter(prev => ({ ...prev, date_to: val }))}
            />
            <TextField
              label="Child Name"
              name="child_name"
              value={filter.child_name}
              onChange={handleChange}
              size="small"
              inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: 'false' }}
            />
            <TextField
              select
              label="Action Type"
              name="type"
              value={filter.type}
              onChange={handleChange}
              size="small"
              style={{ minWidth: 160 }}
              SelectProps={{ native: true }}
            >
              <option value="">All</option>
              {Object.entries(ACTION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </TextField>
            <Button variant="contained" type="submit">Filter</Button>
            <Button variant="outlined" onClick={handleClearFilters}>Clear</Button>
          </div>
        </form>
      </LocalizationProvider>

      <Loader loaded={!loading}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}>ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Organisation</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Child</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((row, index) => (
              <StyledTableRow key={index}>
                <TableCell>{row.id}</TableCell>
                <TableCell>{dateTimeFormatter(row.created_at)}</TableCell>
                <TableCell>{row.organisation_name}</TableCell>
                <TableCell>{row.admin_name}</TableCell>
                <TableCell>{row.child_name}</TableCell>
                <TableCell>{ACTION_TYPE_LABELS[row.type] || row.type}</TableCell>
                <TableCell><ChangesCell data={row.data} /></TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          onPageChange={(_, newPage) => setPage(newPage)}
        />
      </Loader>
    </div>
  );
};

export default inject("Auth")(observer(ActionLogs));

